import React, { useCallback, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import Table from '../../../../components/Table';
import columns from './columnDefenition';
import ContractTypesInit from '../../../../types/initValues/ContractTypes.init';
import BooleanEditor from './components/BooleanEditor';
import BooleanFormatter from './services/booleanFormater';
import TextEditor from './components/TextEditor';
import createContractTypes from '../../../../Apollo/mutations/createContractTypes';
import updateContractTypeQuery from '../../../../Apollo/mutations/updateContractType';
import commitChanges from './commitChanges';
import AlertMessage from '../../../../components/AlertMessage';
import { GET_CONTRACT_TYPES_WITH_LOGO } from '../../../../features/options/api/contractTypeLogo';
import {
  ContractTypeLogoDialogContext,
  ContractTypeLogoEditor,
  ContractTypeLogoFormatter,
} from '../../../../features/options/components/ContractTypeLogoCell';
import ContractTypeLogoDialog from '../../../../features/options/components/ContractTypeLogoDialog';
import type { ContractType } from '../../../../types/ContractType.types';

const booleanColumns = ['is_default'];

function BooleanTypeProvider() {
  return (
    <DataTypeProvider formatterComponent={BooleanFormatter} editorComponent={BooleanEditor} for={booleanColumns} />
  );
}

const textColumns = ['title'];

function TextTypeProvider() {
  return <DataTypeProvider editorComponent={TextEditor} for={textColumns} />;
}

// RITA-1526: Spalte „Logo“ — Vorschau bzw. „kein Logo“, in der Bearbeitungszeile nur lesend.
const logoColumns = ['logo_data_url'];

function LogoTypeProvider() {
  return (
    <DataTypeProvider
      formatterComponent={ContractTypeLogoFormatter}
      editorComponent={ContractTypeLogoEditor}
      for={logoColumns}
    />
  );
}
const Provider = [BooleanTypeProvider, TextTypeProvider, LogoTypeProvider];

// Das Logo wird nicht in der Zeile bearbeitet, sondern zweistufig über den eigenen Dialog.
const editingStateColumnExtensions = [
  { columnName: 'id', editingEnabled: false },
  { columnName: 'logo_data_url', editingEnabled: false },
];

export default function ContractTypsTable() {
  const [createContractType, { data: dataCreateContractType, error: errorCreateContractType, reset: resetCreate }] =
    useMutation(createContractTypes);
  const [updateContractType, { data: dataUpdateContractType, error: errorUpdateContractType, reset: resetUpdate }] =
    useMutation(updateContractTypeQuery);

  // Eigene Abfrage mit logo_data_url; die App-weite Abfrage der Vertragsarten bleibt ohne Logo.
  const { data: ContractTypes, refetch } = useQuery(GET_CONTRACT_TYPES_WITH_LOGO);

  // RITA-1526: Logo-Dialog und seine Erfolgsmeldung („Logo gespeichert“ / „Logo entfernt“).
  const [logoDialogContractType, setLogoDialogContractType] = useState<ContractType | null>(null);
  const [logoMessage, setLogoMessage] = useState<string | null>(null);

  // CONTRACT R-1 (RITA-1390/1394): X-Klick/Auto-Hide muss den Apollo-Mutation-State zurücksetzen,
  // damit dieselbe Erfolgsmeldung nach dem Schließen erneut erscheinen kann.
  const handleCloseAlert = useCallback(() => {
    resetCreate();
    resetUpdate();
    setLogoMessage(null);
  }, [resetCreate, resetUpdate]);

  // Solange die Vertragsarten noch nicht geladen sind, zeigt die Tabelle nur die Platzhalterzeile;
  // deren Zelle bekommt keinen Öffner (Kontext null) und damit keinen Knopf.
  const rowsLoaded = !!ContractTypes;
  const openLogoDialog = useCallback(
    (contractType: ContractType) => {
      resetCreate();
      resetUpdate();
      setLogoMessage(null);
      setLogoDialogContractType(contractType);
    },
    [resetCreate, resetUpdate],
  );
  const closeLogoDialog = useCallback(() => setLogoDialogContractType(null), []);
  const handleLogoSaved = useCallback(
    (message: string) => {
      setLogoDialogContractType(null);
      setLogoMessage(message);
      refetch();
    },
    [refetch],
  );

  const createSucceeded = !!dataCreateContractType && !errorCreateContractType;
  const updateSucceeded = !!dataUpdateContractType && !errorUpdateContractType;
  let alertMessage: string | undefined;
  if (createSucceeded) alertMessage = 'Vertragsart wurde erfolgreich erstellt';
  else if (updateSucceeded) alertMessage = 'Vertragsart erfolgreich geändert';
  else if (logoMessage) alertMessage = logoMessage;

  return (
    <ContractTypeLogoDialogContext.Provider value={rowsLoaded ? openLogoDialog : null}>
      <Table
        usageName="einstellungen.vertragsarten"
        row={ContractTypes ? ContractTypes?.getAllContractTypes : ContractTypesInit}
        columns={columns}
        defaultColumnWidths={columns.map((column) => ({ columnName: column.name, width: 'auto' }))}
        onCommitChanges={({ added, changed }) =>
          commitChanges({
            added,
            changed,
            rows: ContractTypes?.getAllContractTypes,
            refetch,
            createContractType,
            updateContractType,
            resetCreate,
            resetUpdate,
          })
        }
        columnExtensions={editingStateColumnExtensions}
        dataTypeProvider={Provider}
        editable
        showAddCommand
        showEditCommand
      />
      <ContractTypeLogoDialog
        open={logoDialogContractType !== null}
        contractType={logoDialogContractType}
        onClose={closeLogoDialog}
        onSaved={handleLogoSaved}
      />
      <AlertMessage
        typeMessage={alertMessage ? 'success' : undefined}
        alertMessage={alertMessage}
        isAlertMessage={!!alertMessage}
        handleChangeIsAlertMessage={handleCloseAlert}
      />
    </ContractTypeLogoDialogContext.Provider>
  );
}
